import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import Debug from 'debug'
import { useMutation } from '@connectrpc/connect-query'
import { Trans } from '@lingui/react/macro'

import { useAuthenticated } from '@jotta/auth-client/useAuthStatus'
import {
  getCustomerQuery,
  useGetCustomer,
} from '@jotta/grpc-connect-client/customer'
import { authTransport } from '@jotta/grpc-connect-client/transport'
import { WopiActionType, WopiConsent } from '@jotta/grpc-connect-openapi/wopi'
import { updateWopiConsent } from '@jotta/grpc-connect-openapi/wopiQuery'
import { ItemAction } from '@jotta/grpc-web/no/jotta/openapi/file/file.v2_pb'
import type { PathItemObject } from '@jotta/types/Files'
import { Card, CardContent, CardFooter, CardTitle } from '@jotta/ui/Card'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import Spinner from '@jotta/ui/Spinner'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'

import { FileItemTypeIcon } from '../../FileItemTypeIcon/FileItemTypeIcon'

import { getLinksQuery, useWopiLinks } from './useWopiLinks'
import { WopiView } from './WopiView'

const debug = Debug('jotta:files:WopiViewContainer')

export type WopiPostMessageType =
  // https://learn.microsoft.com/en-us/microsoft-365/cloud-storage-partner-program/online/scenarios/postmessage
  | 'App_LoadingStatus'
  | 'File_Rename'
  // The UI_Sharing message is posted when the user activates the Share UI in Office for the web.
  // The host should use this message to trigger any custom sharing UI.
  | 'UI_Sharing'
  // The UI_Close message is posted when the Office for the web application is closing, either due to an error or a user action
  | 'UI_Close'
  // The UI_Edit message is posted when the user activates the Edit UI in Office for the web. This UI is only visible when using the view action.
  | 'UI_Edit'
export type WopiPostMessage = {
  MessageId: WopiPostMessageType
  SendTime: number
  Values: Record<string, string>
}

export function EnableWopi() {
  const { mutate, isPending } = useMutation(updateWopiConsent, {
    transport: authTransport,
    onSuccess: () =>
      Promise.all([getCustomerQuery.invalidate(), getLinksQuery.invalidate()]),
  })

  return (
    <Card className="text-primary">
      <CardTitle>
        <Trans>Use Office Online to preview and edit Office documents</Trans>
      </CardTitle>
      <CardContent>
        <Trans>
          Office documents you preview or edit will be sent to Microsoft Office
          Online for processing while you preview or edit them. You can disable
          Office Online at any time from the settings page.
        </Trans>
      </CardContent>
      <CardFooter className="flex justify-end gap-4">
        <button
          className="btn btn-outline"
          onClick={() =>
            mutate({
              consent: WopiConsent.DECLINED,
            })
          }
        >
          <Trans>Don't ask me again</Trans>
        </button>
        <button
          className="btn btn-primary"
          onClick={() =>
            mutate({
              consent: WopiConsent.ACCEPTED,
            })
          }
        >
          <Trans>Enable</Trans>
        </button>
      </CardFooter>
    </Card>
  )
}

export function Disabled({ item }: { item: PathItemObject }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <FileItemTypeIcon item={item} />
      <a className="link" href="/web/connected">
        <Trans>Enable Office Online to view this document</Trans>
      </a>
    </div>
  )
}

export function DisabledByOwner() {
  return (
    <Card className="text-primary">
      <CardTitle>
        <Trans>Microsoft Office Online is disabled</Trans>
      </CardTitle>
      <CardContent>
        <Trans>
          The owner of this file has disabled access to Microsoft Office Online.
        </Trans>
      </CardContent>
    </Card>
  )
}

function DisabledByOwnerSwitch({ item }: { item: PathItemObject }) {
  const authenticatedOrPending = useAuthenticated() !== 'FALSE'
  const { data, isPending } = useGetCustomer()

  if (authenticatedOrPending && isPending) {
    return <Spinner />
  } else if (item.owner != data?.username) {
    return <DisabledByOwner />
  }

  const wopiConsent = data?.wopiConsent
  switch (wopiConsent) {
    case WopiConsent.NOT_ANSWERED:
    case WopiConsent.UNKNOWN:
      return <EnableWopi />
    case WopiConsent.DECLINED:
    case WopiConsent.ACCEPTED:
      return <Disabled item={item} />
    default:
      return exhaustiveGuard(wopiConsent)
  }
}

export const WopiViewContainer = ({
  item,
  onAction,
}: {
  item: PathItemObject
  onAction?: (action: WopiPostMessageType, data?: string) => void
}) => {
  const [search] = useSearchParams()
  const filePath = item.path
  const fileExtension = filePath.split('.').pop()?.toLowerCase() || ''

  const isTestFile =
    ['wopitest', 'wopitestx'].includes(fileExtension) && WopiActionType.VIEW
  const isNewFile =
    !item.size &&
    item.actionList.includes(ItemAction.UPDATE) &&
    WopiActionType.EDITNEW
  const isEditAction =
    !isNewFile && search.get('action') === 'edit' && WopiActionType.EDIT
  const isLegacyFormat =
    isEditAction &&
    ['doc', 'ppt', 'xls'].includes(fileExtension) &&
    WopiActionType.CONVERT
  const action: WopiActionType =
    isTestFile ||
    isNewFile ||
    isLegacyFormat ||
    isEditAction ||
    WopiActionType.EMBEDVIEW
  const showWopiEditView = action !== WopiActionType.EMBEDVIEW

  useEffect(() => {
    console.group('WopiView')
    return () => {
      console.groupEnd()
    }
  }, [])

  const { data, isRefetching } = useWopiLinks(item, action)

  const result = data?.result

  if (!result || isRefetching) {
    return <LoadingOverlay id="WopiViewContainer" open />
  }

  switch (result.case) {
    case undefined:
      return <LoadingOverlay id="WopiViewContainer" open />
    case 'links':
      return (
        <WopiView
          wopiLinks={result.value}
          item={item}
          onWopiEvent={(e, data) => {
            debug(`onWopiEvent type=${e}`, e, data)
            onAction?.(e, data)
          }}
          showWopiEditView={showWopiEditView}
        />
      )
    case 'disabledByOwner':
      return (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <DisabledByOwnerSwitch item={item} />
        </div>
      )
    default:
      exhaustiveGuard(result)
  }
}
